import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@aaas/shared';
import { registerPortalNamespace } from './i18n/register';
import { LoginPage } from './pages/LoginPage';
import { EmailSentPage } from './pages/EmailSentPage';
import { EmailCallbackPage } from './pages/EmailCallbackPage';
import { HomePage } from './pages/HomePage';
import { WorkspaceSettingsPage } from './pages/WorkspaceSettingsPage';
import { WorkspaceCreatePage } from './pages/WorkspaceCreatePage';
import { WorkspaceJoinPage } from './pages/WorkspaceJoinPage';
import { AssignSeatsPage } from './pages/AssignSeatsPage';
import { WelcomePage } from './pages/WelcomePage';

// Register Portal i18n namespace as a side-effect of importing the routes.
// Shell imports PortalRoutes as a library, so portal/main.tsx never runs —
// without this, `useTranslation('portal')` would receive raw keys.
registerPortalNamespace();

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    // Send to plain /login — do NOT append ?redirect=<previous path>. If a
    // user just signed out (or the next person signs in on this device),
    // bouncing back to the previous user's deep link causes workspace
    // membership errors (e.g. 403 on /ats/dashboard). After login,
    // HomePage handles routing based on the new user's available
    // workspaces (`/welcome` if empty, dashboard otherwise).
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Login route — redirects authenticated users to their original destination
function LoginRoute() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  if (user) {
    return <Navigate to={redirect} replace />;
  }

  return <LoginPage />;
}

export function PortalRoutes() {
  const { loading } = useAuth();

  // Show loading spinner while auth state is being determined
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/auth/email-sent" element={<EmailSentPage />} />
      <Route path="/auth/email-callback" element={<EmailCallbackPage />} />
      <Route
        path="/workspace/settings"
        element={
          <ProtectedRoute>
            <WorkspaceSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/assign-seats"
        element={
          <ProtectedRoute>
            <AssignSeatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/create"
        element={
          <ProtectedRoute>
            <WorkspaceCreatePage />
          </ProtectedRoute>
        }
      />
      {/* /join/:code is a PUBLIC landing route — when the recipient is
          not logged in, WorkspaceJoinPage itself navigates to
          /login?redirect=<full-invite-url> so the invite path is preserved
          across the login flow. Wrapping it in ProtectedRoute would strip
          the path before it can run. */}
      <Route path="/join/:workspaceCode" element={<WorkspaceJoinPage />} />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
